import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { getChefData, updateChefStatus } from "../services/profile-service.js";

const AuthContext = createContext(null);

// Constants for performance and reliability
const TOKEN_KEY = 'authToken';
const FULL_NAME_KEY = 'fullNameHy';
const AUTH_STATE_KEY = 'authState';
const SESSION_VALIDATION_INTERVAL = 5 * 60 * 1000; // 5 minutes - validates session and refreshes chef data
const CHEF_DATA_CACHE_TTL = 2 * 60 * 1000; // 2 minutes cache

/**
 * AuthProvider - Unified authentication and user context provider
 * 
 * Features:
 * - Single source of truth for auth and user data
 * - Automatic session validation (validates token and refreshes chef data every 5 minutes)
 * - Optimized state management with memoization
 * - Error handling and recovery
 * - localStorage persistence
 * - Performance optimizations (target: <50ms for state updates)
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} AuthContext provider
 */
export function AuthProvider({ children }) {
    // Core auth state
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Chef/user data
    const [chefData, setChefData] = useState(null);
    const [fullNameAm, setFullNameAm] = useState('');
    const [chefId, setChefId] = useState(null);
    const [isChefActive, setIsChefActive] = useState(true);
    
    // Performance optimizations: use refs to prevent unnecessary re-renders
    const refreshIntervalRef = useRef(null);
    const lastFetchTimeRef = useRef(0);
    const isFetchingRef = useRef(false);
    const abortControllerRef = useRef(null);

    /**
     * Initialize auth state from localStorage (fast, <10ms)
     */
    useEffect(() => {
        const initAuthState = () => {
            try {
                const token = localStorage.getItem(TOKEN_KEY);
                const savedFullName = localStorage.getItem(FULL_NAME_KEY);
                const savedState = localStorage.getItem(AUTH_STATE_KEY);
                
                if (token) {
                    setIsAuthenticated(true);
                    if (savedFullName) {
                        setFullNameAm(savedFullName);
                    }
                    if (savedState) {
                        try {
                            const parsed = JSON.parse(savedState);
                            if (parsed.chefId) setChefId(parsed.chefId);
                            if (parsed.isChefActive !== undefined) setIsChefActive(parsed.isChefActive);
                        } catch (e) {
                            // Ignore parse errors
                        }
                    }
                }
            } catch (err) {
                console.error('Error initializing auth state:', err);
            } finally {
                setIsLoading(false);
            }
        };
        
        initAuthState();
    }, []);

    /**
     * Check authentication status and fetch chef data
     * Optimized to prevent duplicate calls and respect cache TTL
     * Target: <200ms for cached data, <500ms for fresh fetch
     */
    const checkAuth = useCallback(async (forceRefresh = false) => {
        const token = localStorage.getItem(TOKEN_KEY);
        const now = Date.now();
        
        // Fast path: no token
        if (!token) {
            setIsAuthenticated(false);
            setChefData(null);
            setIsLoading(false);
            setError(null);
            return;
        }

        // Fast path: use cached data if available and fresh
        if (!forceRefresh && chefData && (now - lastFetchTimeRef.current) < CHEF_DATA_CACHE_TTL) {
            setIsAuthenticated(true);
            setIsLoading(false);
            setError(null);
            return;
        }

        // Prevent duplicate concurrent requests
        if (isFetchingRef.current) {
            return;
        }

        isFetchingRef.current = true;
        setError(null);
        
        // Abort previous request if exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
            const data = await getChefData(forceRefresh);
            
            // Update state atomically for performance
            setIsAuthenticated(true);
            setChefData(data);
            lastFetchTimeRef.current = now;
            
            // Update derived state
            if (data?.fullNameHy) {
                setFullNameAm(data.fullNameHy);
                localStorage.setItem(FULL_NAME_KEY, data.fullNameHy);
            }
            if (data?.id) {
                setChefId(data.id);
            }
            if (data?.status !== undefined) {
                setIsChefActive(data.status === "ACTIVE");
            }
            
            // Persist state to localStorage
            try {
                localStorage.setItem(AUTH_STATE_KEY, JSON.stringify({
                    chefId: data?.id,
                    isChefActive: data?.status === "ACTIVE"
                }));
            } catch (e) {
                // Ignore localStorage errors
            }
            
            // Set loading to false only after chefData is successfully loaded
            setIsLoading(false);
            
        } catch (err) {
            // Handle auth errors
            if (err.response?.status === 401 || err.response?.status === 403) {
                // Clear auth state on unauthorized
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(FULL_NAME_KEY);
                localStorage.removeItem(AUTH_STATE_KEY);
                setIsAuthenticated(false);
                setChefData(null);
                setFullNameAm('');
                setChefId(null);
            } else {
                setError(err);
            }
            // Set loading to false on error as well
            setIsLoading(false);
        } finally {
            isFetchingRef.current = false;
            abortControllerRef.current = null;
        }
    }, [chefData]);

    /**
     * Refresh chef data (optimized wrapper)
     * Target: <500ms
     */
    const refreshChefData = useCallback(async () => {
        return checkAuth(true);
    }, [checkAuth]);

    /**
     * Logout user
     * Target: <10ms
     */
    const logout = useCallback(() => {
        // Clear all auth-related data
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(FULL_NAME_KEY);
        localStorage.removeItem(AUTH_STATE_KEY);
        
        // Clear interval
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }
        
        // Abort any pending requests
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        
        // Reset state
        setIsAuthenticated(false);
        setChefData(null);
        setFullNameAm('');
        setChefId(null);
        setIsChefActive(true);
        setError(null);
        isFetchingRef.current = false;
        lastFetchTimeRef.current = 0;
    }, []);

    /**
     * Set chef info (optimized for performance)
     * Target: <5ms
     */
    const chefInfo = useCallback((name, id) => {
        setFullNameAm(name);
        setChefId(id);
        localStorage.setItem(FULL_NAME_KEY, name);
    }, []);

    /**
     * Update chef data manually
     * Target: <10ms
     */
    const setChefDataManual = useCallback((data) => {
        if (data?.fullNameHy) {
            chefInfo(data.fullNameHy, data.id);
        }
        if (data?.status !== undefined) {
            setIsChefActive(data.status === "ACTIVE");
        }
        if (data) {
            setChefData(data);
            lastFetchTimeRef.current = Date.now();
        }
    }, [chefInfo]);

    /**
     * Update chef active status
     * Target: <300ms (API call)
     */
    const updateChefActiveStatus = useCallback(async (newStatus) => {
        if (!chefId) return false;

        try {
            await updateChefStatus(chefId, newStatus);
            setIsChefActive(newStatus === "ACTIVE");
            
            // Update chefData if available
            if (chefData) {
                setChefData(prev => ({
                    ...prev,
                    status: newStatus
                }));
            }

            // Refresh to get latest from server
            await refreshChefData();

            return true;
        } catch (error) {
            console.error("Failed to update chef status:", error);
            setError(error);
            return false;
        }
    }, [chefId, chefData, refreshChefData]);

    /**
     * Initial auth check on mount
     */
    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
            checkAuth();
        } else {
            setIsLoading(false);
        }
    }, [checkAuth]);

    /**
     * Session validation mechanism - validates token and refreshes chef data periodically
     * Only runs when authenticated and page is visible
     * Note: This validates the session, not refresh the token (no refresh token endpoint)
     */
    useEffect(() => {
        if (!isAuthenticated) {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
            return;
        }

        // Set up interval for session validation
        refreshIntervalRef.current = setInterval(() => {
            // Only validate if page is visible
            if (!document.hidden) {
                checkAuth(false); // Use cache if available, validates token via API call
            }
        }, SESSION_VALIDATION_INTERVAL);

        // Cleanup on unmount or when auth changes
        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
    }, [isAuthenticated, checkAuth]);

    /**
     * Refresh on page visibility change (when user returns to tab)
     */
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && isAuthenticated) {
                // Refresh if cache is stale
                const now = Date.now();
                if ((now - lastFetchTimeRef.current) > CHEF_DATA_CACHE_TTL) {
                    checkAuth(false);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isAuthenticated, checkAuth]);

    /**
     * Memoized context value for performance
     * Prevents unnecessary re-renders of consumers
     * Target: <1ms for value creation
     */
    const contextValue = useMemo(() => ({
        // Auth state
        isAuthenticated,
        isLoading,
        error,
        
        // Chef/user data
        chefData,
        fullNameAm,
        chefId,
        isChefActive,
        
        // Methods
        checkAuth,
        logout,
        refreshChefData,
        chefInfo,
        setChefData: setChefDataManual,
        updateChefActiveStatus
    }), [
        isAuthenticated,
        isLoading,
        error,
        chefData,
        fullNameAm,
        chefId,
        isChefActive,
        checkAuth,
        logout,
        refreshChefData,
        chefInfo,
        setChefDataManual,
        updateChefActiveStatus
    ]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}

/**
 * Hook to access auth context
 * Throws error if used outside AuthProvider
 * 
 * @returns {Object} Auth context value
 * @throws {Error} If used outside AuthProvider
 */
export function useAuth() {
    const context = useContext(AuthContext);
    
    if (context === null) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    
    return context;
}

/**
 * Legacy hook name for backward compatibility during migration
 * @deprecated Use useAuth instead
 */
export const useUserContext = useAuth;

